import { EmptyState, Spinner } from "@qn/ui";
import { useQuery } from "@tanstack/react-query";
import { PanelLeft, Shapes } from "lucide-react";
import { ArtifactPanel, TYPE_ICON } from "../components/ArtifactPanel";
import { api } from "../lib/api";
import type { ArtifactMeta } from "../lib/artifacts";
import { useT } from "../lib/i18n";
import { usePrefs } from "../lib/prefs";
import { useUI } from "../lib/ui-store";

export function ArtifactsPage() {
  const t = useT();
  const { artifactId, openArtifact } = useUI();
  const { sidebarOpen, set } = usePrefs();
  const q = useQuery({
    queryKey: ["artifacts"],
    queryFn: () => api<{ artifacts: ArtifactMeta[] }>("/artifacts"),
  });
  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--line)] px-3">
          <button
            type="button"
            onClick={() => set({ sidebarOpen: !sidebarOpen })}
            aria-label={t("sidebar.toggle")}
            className={`grid size-9 place-items-center rounded-lg hover:bg-[var(--surface-3)] ${sidebarOpen ? "lg:hidden" : ""}`}
          >
            <PanelLeft className="size-5 rtl:-scale-x-100" />
          </button>
          <h1 className="font-display text-lg font-semibold">{t("nav.artifacts")}</h1>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {q.isLoading ? (
            <div className="grid place-items-center py-16">
              <Spinner />
            </div>
          ) : !q.data?.artifacts.length ? (
            <EmptyState
              icon={<Shapes className="size-6" />}
              title={t("artifacts.emptyTitle")}
              hint={t("artifacts.emptyHint")}
            />
          ) : (
            <ul className="mx-auto grid max-w-4xl gap-3 sm:grid-cols-2">
              {q.data.artifacts.map((a) => {
                const Icon = TYPE_ICON[a.type];
                return (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => openArtifact(a.id)}
                      className="flex w-full items-center gap-3 rounded-xl border border-[var(--line)] bg-[var(--surface)] p-3.5 text-start transition hover:border-[var(--accent-ring)]"
                    >
                      <span className="grid size-10 place-items-center rounded-lg bg-[var(--accent-soft)] text-[var(--accent-text)]">
                        <Icon className="size-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium" dir="auto">
                          {a.title}
                        </span>
                        <span className="block text-xs text-[var(--fg-faint)]">
                          {t(`artifact.type.${a.type}`)} · v{a.version} ·{" "}
                          {new Date(a.updatedAt).toLocaleDateString()}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      {artifactId && <ArtifactPanel id={artifactId} />}
    </div>
  );
}
